import { AbstractModal } from '../AbstractModal';
import type { HexMapState } from '../../engine/render/renderer';
import type { ContentBuilder } from '../ui/ContentBuilder';
import type { Tile } from '../../engine/hex/types';
import { t } from '../../i18n/translations';
import {
    ACTION__SCOUT__PLAN_ALTER,
    ACTION__SCOUT__PLAN_CLEAR,
    ACTION__SCOUT__RETURN,
    ACTION__UI__OPEN_BATTLE_REPORT,
} from '../../engine/input/actionIds';
import { startReturnMission } from '../../engine/scouting/scouting';
import { BattleReportModal } from '../battle/BattleReportModal';

export class ScoutingMissionModal extends AbstractModal {
    private missionId: string;

    constructor(
        rect: { x: number; y: number; w: number; h: number },
        tile: Tile,
        missionId: string,
    ) {
        super(tile, rect, null);
        this.id = `scouting_mission_${missionId}`;
        this.missionId = missionId;
        this.showTileHighlight = true;
        this.allowCloseDuringTutorial = true;
    }

    getHeaderLabel(state: HexMapState): string {
        return t(state.config.ui.language, 'scout.missionTitle');
    }

    handleAction(actionId: string, state: HexMapState): boolean {
        const mission = state.scoutingMissions.find((entry) => entry.id === this.missionId);
        if (!mission) return false;
        if (actionId === ACTION__SCOUT__PLAN_ALTER) {
            if (mission.status === 'complete') return false;
            state.scoutPlanningMissionId = mission.id;
            return true;
        }
        if (actionId === ACTION__SCOUT__PLAN_CLEAR) {
            mission.plan = [];
            return true;
        }
        if (actionId === ACTION__SCOUT__RETURN) {
            return startReturnMission(mission, state.config.scouting);
        }
        if (actionId.startsWith(`${ACTION__UI__OPEN_BATTLE_REPORT}:`)) {
            const reportId = actionId.slice(`${ACTION__UI__OPEN_BATTLE_REPORT}:`.length);
            const modalId = `battle_report_${reportId}`;
            const existing = state.modals.find((modal) => modal.id === modalId);
            if (existing) return false;
            const rect = { x: state.pointer.x - 120, y: state.pointer.y - 60, w: 320, h: 360 };
            state.modals.push(new BattleReportModal(rect, reportId));
            state.activeModalId = modalId;
            return true;
        }
        return false;
    }

    buildContent(state: HexMapState, builder: ContentBuilder): void {
        const lang = state.config.ui.language;
        const mission = state.scoutingMissions.find((entry) => entry.id === this.missionId);
        if (!mission) {
            builder.text(t(lang, 'scout.missionMissing'));
            return;
        }
        builder.section(t(lang, 'scout.missionStatus'), (section) => {
            section.text(t(lang, 'scout.missionTarget', mission.targetTileId));
            section.text(t(lang, `scout.missionState.${mission.status}`));
            if (mission.status === 'traveling' || mission.status === 'returning') {
                section.text(
                    t(lang, 'scout.missionProgress', mission.progress, mission.travelTicks),
                );
            }
            if (mission.status === 'battle' && mission.battle) {
                const totalTicks = mission.battle.totalRounds * mission.battle.ticksPerRound;
                const currentTicks =
                    mission.battle.roundIndex * mission.battle.ticksPerRound +
                    mission.battle.roundTick;
                section.text(t(lang, 'scout.missionBattleProgress', currentTicks, totalTicks));
            }
        });

        builder.section(t(lang, 'scout.missionPlan'), (section) => {
            if (mission.plan.length === 0) {
                section.text(t(lang, 'scout.missionPlanEmpty'));
            } else {
                section.text(t(lang, 'scout.missionPlanNext', mission.plan[0]));
            }
            section.button(
                ACTION__SCOUT__PLAN_ALTER,
                t(lang, 'scout.missionPlanAlter'),
                mission.status !== 'complete',
            );
            section.button(
                ACTION__SCOUT__PLAN_CLEAR,
                t(lang, 'scout.missionPlanClear'),
                mission.plan.length > 0,
            );
            section.text(t(lang, 'scout.missionPlanHint'), 'muted');
        });

        if (mission.status === 'holding') {
            builder.section(t(lang, 'scout.missionOrders'), (section) => {
                section.button(ACTION__SCOUT__RETURN, t(lang, 'scout.missionReturn'), true);
            });
        }

        if (mission.battleReportId) {
            builder.section(t(lang, 'scout.missionReport'), (section) => {
                section.button(
                    `${ACTION__UI__OPEN_BATTLE_REPORT}:${mission.battleReportId}`,
                    t(lang, 'scout.missionReportOpen'),
                    true,
                );
            });
        }
    }
}
